import gql from 'graphql-tag';

import {
  FRAGMENT_CONTENT_VALIDATION,
  FRAGMENT_IMAGE_OVERLAY_TEXT,
  FRAGMENT_PLAGIARISM_REPORT,
} from 'src/graphql/schema';
import { VARIATION_EXPORT_INFO_FRAGMENT } from 'src/graphql/variation/schema';

export const PROJECT_FROM_LIST_FRAGMENT = gql`
  fragment ProjectFromList on Project {
    id
    type
    name
    status
    editedAt
    generatedGenerationTools
    projectFolder {
      id
    }
    lastModifyingUser {
      name
      email
    }
    favorite
  }
`;

export const PROJECT_FRAGMENT = gql`
  fragment Project on Project {
    id
    type
    name
    status
    editedAt
    generatedGenerationTools
    editorContent
    workspaceId
    accessState
    projectFolder {
      id
    }
    customerPersonas {
      brandVoice {
        id
        name
      }
      id
      name
    }
    contentTaxonomy {
      id
      name
    }
    lastModifyingUser {
      name
      email
    }
    state
    brandVoiceIdsForBrandTerms
  }
`;

export const FRAGMENT_PROJECT_FOR_SEARCH = gql`
  fragment ProjectForSearch on Project {
    id
    name
    type
    lastModifyingUser {
      name
      email
    }
    projectFolder {
      id
      name
    }
    graphqlType
  }
`;

export const FRAGMENT_PROJECT_SECTION = gql`
  fragment ProjectSectionFields on ProjectSection {
    id
    name
    createdAt
    autoNamePending
  }
`;

export const FRAGMENT_ASSET_SCORING_METHOD = gql`
  fragment AssetScoringMethod on ScoringMethod {
    id
    type
    name
  }
`;

export const FRAGMENT_ASSET_MEDIA_INFO = gql`
  fragment AssetMediaInfo on MediaInfo {
    type
    path
    imageStyleId
    imageDescription
    imageScore
    resourceId
  }
`;

export const FRAGMENT_ASSET_COMMON = gql`
  fragment AssetCommon on Asset {
    id
    assetGroupId
    createdAt
    score
    ageScoreHistogram
    genderScoreHistogram
    engagementScoreSupportSize
    contentTaxonomyId
    isScoreLocked
    performancePredictionsScoreLocked
    isEnterpriseScoreUsed
    scoringMethod {
      ...AssetScoringMethod
    }
    secondaryScoringMethod {
      ...AssetScoringMethod
    }
  }
  ${FRAGMENT_ASSET_SCORING_METHOD}
`;

export const FRAGMENT_SHORT_FORM_ASSET = gql`
  fragment ShortFormAssetFields on ShortFormAsset {
    copyText
    copyTextMarkup
    assetType
    generationTool
    classifiedGenerationTool
    multiPromptTool
    language
    rating
    mixtureSubAssetType
    groupIdx
    originalVariationId
    source
    suggestionMode
    recomposeMode
    lastUpdateTime
    isEdited
    customFormatIds
    imagePath
    imageBlob
    imageMeta
    imageStyleId
    overlayText {
      ...ImageOverlayText
    }
    complianceStatus
    complianceProgressPercentage
    contentSafety {
      ...ContentValidation
    }
    benchmarkConfig {
      userCreated
      account {
        id
        name
        channel
      }
      assetType
      comparisonSet {
        name
        description
        userGoal
        examples
      }
    }
    generationGuidance {
      keywords
      campaignKeywords
      features
      benefits
      pains
      avatar
      tov
    }
    exportInfo {
      ...VariationExportInfo
    }

    # Not read by the card — these are what isShortFormAsset discriminates on, since
    # addTypename:false leaves __typename null on the assets union.
    projectAiSuggestionRequestId
    projectAiRecomposeRequestId
  }
  ${FRAGMENT_CONTENT_VALIDATION}
  ${FRAGMENT_IMAGE_OVERLAY_TEXT}
  ${VARIATION_EXPORT_INFO_FRAGMENT}
`;

export const FRAGMENT_LONG_FORM_ASSET = gql`
  fragment LongFormAssetFields on LongFormAsset {
    name
    contentHtml
    assetType
    generationTool
    textStatus
    originalLongFormAssetId
    contentMarkup {
      start
      end
      type
      paragraph
    }
    prompt
    promptData
    structureInstructions
    toneOfVoice
    language
    imageAspectRatio
    imageStyleId
    complianceStatus
    complianceProgressPercentage
    mediaInfo {
      ...AssetMediaInfo
    }
  }
  ${FRAGMENT_ASSET_MEDIA_INFO}
`;

export const FRAGMENT_LONG_FORM_ASSET_FULL = gql`
  fragment LongFormAssetFull on LongFormAsset {
    ...AssetCommon
    ...LongFormAssetFields
  }
  ${FRAGMENT_ASSET_COMMON}
  ${FRAGMENT_LONG_FORM_ASSET}
`;

/**
 * The improve an asset last came back from. Selected ONLY alongside a job's own asset groups: asked of
 * a section the user merely opened, it would just as happily describe an improve from weeks ago, and
 * every asset that ever came back empty would grow a notice again on each load. Adding it to
 * `FRAGMENT_SHORT_FORM_ASSET`, or to `promoteLowerScoringAssets` — whose response is the card a
 * recovery just settled — puts it exactly where it does not belong.
 */
export const FRAGMENT_SHORT_FORM_ASSET_IMPROVE_RESULT = gql`
  fragment ShortFormAssetImproveResult on ShortFormAsset {
    lastImproveResult {
      recomposeRequestId
      producedAssetIds
      failureReason
      reportedReason
      blacklistMatches {
        term
        brandVoiceId
      }
      isEligibleToRetry
      lowerScoringResultsAvailable
    }
  }
`;

export const FRAGMENT_ASSET_GROUP = gql`
  fragment AssetGroupFields on AssetGroup {
    id
    type
    status
    contentLabel
    contentType
    createdAt
    section {
      id
    }
    sourceType
    generationTool
    chainGenerationTool
    customTemplate {
      id
      name
    }
    customerPersona {
      id
      name
    }
    subTemplate {
      id
      name
    }
    blogKeywords {
      targetKeywords
      supportingKeywords
    }
    lastPlagiarismCheck {
      ...PlagiarismReportFields
    }
    assets {
      ...AssetCommon
      ...ShortFormAssetFields
      ...LongFormAssetFields
    }
  }
  ${FRAGMENT_ASSET_COMMON}
  ${FRAGMENT_SHORT_FORM_ASSET}
  ${FRAGMENT_LONG_FORM_ASSET}
  ${FRAGMENT_PLAGIARISM_REPORT}
`;
